import { waitForTransactionReceipt as waitForTransactionOrigin } from '@wagmi/core';
import { sleep } from 'utils/sleep';
import { retryAsync } from 'utils/retryAsync';
import {
  BASE_AVERAGE_BLOCK_TIME,
  ETH_AVERAGE_BLOCK_TIME,
} from 'utils/constants/ethConst';
import SafeAppsSDK, { TransactionStatus } from '@safe-global/safe-apps-sdk';
import {
  CONNECTOR_IDS,
  ConnectorID,
  wagmiConfig,
} from 'structure/providers/wagmiConfig';

const timeoutMap: Record<number, number> = {
  8453: BASE_AVERAGE_BLOCK_TIME,
  1: ETH_AVERAGE_BLOCK_TIME,
};

type WaitForTransactionOptions = {
  chainId?: number;
  hash: `0x${string}`;
  sleepMs?: number;
  retries?: number;
  timeout?: number;
  retryDelayMs?: number | ((attempt: number) => number);
};

export const waitForTransaction = async (
  params: WaitForTransactionOptions & { connectorId?: ConnectorID | string }
) => {
  const { connectorId, ...rest } = params;

  if (connectorId === CONNECTOR_IDS.SAFE_WALLET) {
    return waitForTransactionSafeWallet(rest);
  }

  return _waitForTransaction(rest);
};

// sometimes RPC does not work properly and returns an error or goes into an infinite loop, I assume this is a problem with public RPC - it does not synchronize with the blockchain.
// wagmi must be updated to LTS version: mb it will fix the issue
const _waitForTransaction = async (params: WaitForTransactionOptions) => {
  const {
    retries,
    retryDelayMs,
    sleepMs = timeoutMap[params.chainId || 1] || ETH_AVERAGE_BLOCK_TIME,
    // set default timeout to remove infinite loop, request will crash
    timeout = timeoutMap[params.chainId || 1] || ETH_AVERAGE_BLOCK_TIME,
    ...waitForTransactionArgs
  } = params;

  // wait for the next block before starting to wait
  await sleep(sleepMs);
  return retryAsync(
    () =>
      waitForTransactionOrigin(wagmiConfig, {
        ...waitForTransactionArgs,
        timeout,
      }),
    retries,
    retryDelayMs
  );
};

const waitForTransactionSafeWallet = async (
  params: WaitForTransactionOptions
) => {
  const { hash } = params;

  const safeSdk = new SafeAppsSDK();

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const queued = await safeSdk.txs.getBySafeTxHash(hash);
    if (
      queued.txStatus === TransactionStatus.AWAITING_CONFIRMATIONS ||
      queued.txStatus === TransactionStatus.AWAITING_EXECUTION
    ) {
      await sleep(ETH_AVERAGE_BLOCK_TIME);
      continue;
    }

    if (queued.txStatus === TransactionStatus.CANCELLED) {
      throw new Error('Transaction canceled');
    }

    if (queued.txStatus === TransactionStatus.FAILED) {
      throw new Error('Transaction failed');
    }

    if (queued.txStatus === TransactionStatus.SUCCESS && queued.txHash) {
      return _waitForTransaction({
        ...params,
        hash: queued.txHash as `0x${string}`, // override txHash
      });
    }

    throw new Error('waitForTransactionSafeWallet unhandled error');
  }
};
